/** Beta Logistics — a case stalled in data collection. Its point is the gap. */
import { dollars } from "@/lib/format";
import type { Borrower, CaseRecord } from "@/lib/types";

const FS = { documentId: "doc-beta-fs", documentName: "2025 Financial Statements (draft)" };

export const betaBorrower: Borrower = {
  id: "beta",
  name: "Beta Logistics",
  industry: "Regional freight and warehousing",
  relationshipSince: 2025,
  riskGrade: "B-",
  totalExposure: dollars(220_000),
  headquarters: "Memphis, Tennessee",
  employees: 41,
};

export const betaCase: CaseRecord = {
  case: {
    id: "beta-2026-wc",
    reference: "CR-2026-0447",
    borrowerId: "beta",
    borrowerName: "Beta Logistics",
    purpose: "Working Capital",
    requestedAmount: dollars(220_000),
    stage: "data_collection",
    riskGrade: "B-",
    openedAt: "2026-08-18T11:40:00Z",
    owner: "Tom Giladi",
    summary: [
      "Underwriting cannot start. The March 2026 bank statement is missing, which breaks the twelve-month deposit series the cash-flow model requires.",
      "What has arrived looks ordinary for the sector: 8.4% revenue growth, thin but positive margins, no adverse public records.",
    ],
    risks: [
      { id: "beta-risk-gap", label: "March 2026 bank statement missing — model cannot run" },
      { id: "beta-risk-margin", label: "Operating margin 4.1%, below the 6% sector median" },
    ],
    strengths: [
      { id: "beta-strength-growth", label: "Revenue growth 8.4% year over year" },
      { id: "beta-strength-records", label: "No liens, judgments or adverse filings" },
    ],
    headlineMetrics: [
      { id: "beta-m-revenue", label: "Revenue", value: "$2.14M" },
      { id: "beta-m-ebitda", label: "EBITDA", value: "$88K" },
      { id: "beta-m-dscr", label: "DSCR", value: "Not calculable" },
      { id: "beta-m-leverage", label: "Debt / EBITDA", value: "3.10x" },
    ],
    openQuestions: [
      {
        id: "beta-q-march",
        question: "Obtain the March 2026 bank statement",
        target: { tab: "evidence", anchor: "doc-beta-bank" },
        resolved: false,
      },
      {
        id: "beta-q-audit",
        question: "Confirm whether the 2025 statements are final or draft",
        target: { tab: "evidence", anchor: "doc-beta-fs" },
        resolved: false,
      },
    ],
  },

  evidence: [
    {
      id: "doc-beta-bank",
      caseId: "beta-2026-wc",
      kind: "banking",
      name: "Bank Statements — Aug 2025 to Jul 2026",
      provider: "Manual upload · First Horizon",
      receivedAt: "2026-08-19T09:10:00Z",
      status: "missing",
      extracted: [
        {
          id: "ev-beta-months",
          label: "Months received",
          value: "11 of 12",
          confidence: 1,
          status: "flagged",
          section: "Coverage",
          note: "March 2026 is absent. The cash-flow model requires a contiguous twelve-month series.",
        },
        {
          id: "ev-beta-deposits",
          label: "Average monthly deposits",
          value: "$178,300",
          amount: dollars(178_300),
          confidence: 0.88,
          status: "unverified",
          section: "Deposit summary",
          note: "Computed across 11 months. Not comparable to policy until March is supplied.",
        },
      ],
    },
    {
      id: "doc-beta-fs",
      caseId: "beta-2026-wc",
      kind: "document",
      name: "2025 Financial Statements (draft)",
      provider: "Borrower upload",
      receivedAt: "2026-08-18T12:05:00Z",
      pages: 11,
      status: "stale",
      extracted: [
        {
          id: "ev-beta-revenue",
          label: "Revenue",
          value: "$2,140,000",
          amount: dollars(2_140_000),
          confidence: 0.91,
          status: "unverified",
          page: 4,
          section: "Statement of operations",
          note: "Marked DRAFT on every page. Confirm the final version before decisioning.",
        },
        {
          id: "ev-beta-ebitda",
          label: "EBITDA",
          value: "$88,000",
          amount: dollars(88_000),
          confidence: 0.87,
          status: "unverified",
          page: 5,
          section: "Statement of operations",
        },
      ],
    },
  ],

  analysis: {
    caseId: "beta-2026-wc",
    financials: {
      periods: ["2023", "2024", "2025"],
      rows: [
        {
          id: "beta-fin-revenue",
          label: "Revenue",
          values: { "2023": "$1.82M", "2024": "$1.97M", "2025": "$2.14M" },
          emphasis: true,
          trace: {
            claim: "Revenue $2.14M in 2025",
            derivation: ["Draft statement of operations"],
            evidence: { ...FS, page: 4, section: "Statement of operations", extractedValueId: "ev-beta-revenue" },
          },
        },
        {
          id: "beta-fin-ebitda",
          label: "EBITDA",
          values: { "2023": "$79K", "2024": "$84K", "2025": "$88K" },
          emphasis: true,
        },
        { id: "beta-fin-margin", label: "EBITDA margin", values: { "2023": "4.3%", "2024": "4.3%", "2025": "4.1%" } },
        { id: "beta-fin-debt", label: "Total debt", values: { "2023": "$198K", "2024": "$241K", "2025": "$273K" } },
      ],
    },
    bridge: { reportedLabel: "Reported EBITDA", reported: dollars(88_000), adjustments: [] },
    models: [
      {
        id: "beta-model-credit",
        name: "Credit Model",
        version: "v4",
        result: "Blocked",
        tone: "negative",
        ranAt: "2026-08-19T09:12:00Z",
        drivers: ["Incomplete deposit series — March 2026 missing"],
      },
      {
        id: "beta-model-fraud",
        name: "Fraud Model",
        version: "v1",
        result: "Low",
        tone: "positive",
        ranAt: "2026-08-19T09:12:00Z",
        drivers: ["No entity-graph anomalies"],
      },
    ],
    cashFlow: [
      { month: "Feb 2026", inflow: dollars(181_200), outflow: dollars(174_800), closing: dollars(62_400) },
      { month: "Mar 2026", inflow: 0, outflow: 0, closing: 0, note: "Statement not provided." },
      { month: "Apr 2026", inflow: dollars(176_500), outflow: dollars(169_300), closing: dollars(71_100) },
      { month: "May 2026", inflow: dollars(183_900), outflow: dollars(177_400), closing: dollars(77_600) },
    ],
    forecast: [],
    covenantHeadroom: [],
  },

  decision: {
    caseId: "beta-2026-wc",
    kind: "more_info",
    amount: dollars(220_000),
    termMonths: 24,
    pricing: "Prime + 7.4% (indicative)",
    riskGrade: "B-",
    confidence: "low",
    positives: [{ id: "beta-pos-growth", label: "Revenue growth 8.4% year over year" }],
    negatives: [
      { id: "beta-neg-gap", label: "March 2026 bank statement missing" },
      { id: "beta-neg-draft", label: "2025 financial statements are unsigned drafts" },
    ],
    conditions: [],
    outcome: null,
  },

  activity: [
    {
      id: "act-beta-1",
      at: "2026-08-19T09:12:00Z",
      actor: "Credit Model v4",
      kind: "model",
      title: "Credit Model v4 could not complete",
      detail: "Incomplete deposit series — March 2026 missing",
    },
    {
      id: "act-beta-2",
      at: "2026-08-19T09:10:00Z",
      actor: "Tom Giladi",
      kind: "evidence",
      title: "Bank statements uploaded",
      detail: "11 of 12 months · March 2026 absent",
    },
    {
      id: "act-beta-3",
      at: "2026-08-18T11:40:00Z",
      actor: "Tom Giladi",
      kind: "stage",
      title: "Case opened",
      detail: "Working Capital · $220,000 requested",
    },
  ],
};
